import { Link, useLocation } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { site } from "@/lib/site";
import logo from "@/assets/ccob-logo.webp";
import logo96 from "@/assets/ccob-logo-96.webp";
import logo192 from "@/assets/ccob-logo-192.webp";

type NavItem =
  | { to: string; label: string; children?: never }
  | { to?: string; label: string; children: { to: string; label: string }[] };

const nav: NavItem[] = [
  { to: "/", label: "Home" },
  {
    to: "/about",
    label: "About",
    children: [
      { to: "/about", label: "Overview" },
      { to: "/reviews", label: "Client Reviews" },
    ],
  },
  {
    to: "/services",
    label: "Services",
    children: [
      { to: "/services", label: "All Services" },
      { to: "/services/accounting", label: "Accounting" },
      { to: "/services/bookkeeping", label: "Bookkeeping" },
      { to: "/services/payroll", label: "Payroll" },
      { to: "/services/small-business", label: "Small Business" },
      { to: "/services/business-formation", label: "Business Formation" },
      { to: "/services/auditing", label: "Auditing" },
      { to: "/services/taxation", label: "Taxation" },
      { to: "/services/tax-preparation", label: "Tax Preparation" },
      { to: "/services/tax-planning", label: "Tax Planning" },
      { to: "/services/part-time-cfo", label: "Part-Time CFO" },
    ],
  },
  { to: "/tax-center", label: "Tax Center" },
  {
    to: "/resources",
    label: "Resources",
    children: [
      { to: "/resources", label: "Overview" },
      { to: "/calculators", label: "Financial Calculators" },
      { to: "/links", label: "Useful Links" },
    ],
  },
  { to: "/blog", label: "Blog" },
  { to: "/reviews", label: "Reviews" },
  { to: "/cities-we-serve", label: "Service Areas" },
  { to: "/terms", label: "T&C" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  // Routes without a dark cover hero — header sits on a light background, so use dark text
  const lightBgRoutes = [/^\/$/, /^\/blog\//];
  const isLightBg = lightBgRoutes.some((r) => r.test(pathname));
  const dark = scrolled;
  // Cover hero is dark on every page except light-bg routes — use light text when transparent
  const lightText = dark || !isLightBg;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); if (typeof window !== "undefined" && !window.location.hash) window.scrollTo({ top: 0 }); }, [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        !dark && isHome ? "lg:border-transparent" : ""
      }`}
    >
      <div
        aria-hidden
        className={`absolute inset-0 -z-10 transition-all duration-500 ${
          dark ? "bg-primary/95 backdrop-blur-md shadow-md" : "bg-transparent"
        }`}
      />
      <div className="nav-container-x flex items-center justify-between h-16 lg:h-20 relative">
        <Link to="/" className="flex items-center gap-3 relative z-10" aria-label="CCOB And Company PA home">
          <img
            src={logo}
            srcSet={`${logo96} 96w, ${logo192} 192w, ${logo} 300w`}
            sizes="(max-width: 1023px) 128px, 186px"
            alt="CCOB & Company PA"
            width={300}
            height={300}
            decoding="async"
            fetchPriority="high"
            loading="eager"
            style={!scrolled ? { transformOrigin: "top left" } : undefined}
            className={`w-auto object-contain transition-all duration-300 logo-fade-in ${
              scrolled
                ? "h-[47px] lg:h-[54px] max-w-[180px] sm:max-w-[210px] lg:max-w-[245px]"
                : "h-[128px] lg:h-[186px] -mb-[88px] lg:-mb-[120px] max-w-none"
            }`}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-5 2xl:gap-8 whitespace-nowrap">
          {nav.map((n) => {
            const linkClass = `text-sm font-medium transition-colors hover:text-gold inline-flex items-center gap-1 min-h-[44px] px-1 ${
              lightText ? "text-primary-foreground/90" : "text-foreground"
            }`;
            if (n.children) {
              const trigger = n.to ? (
                <Link
                  to={n.to}
                  className={linkClass}
                  activeProps={{ className: lightText ? "text-primary-foreground" : "text-primary" }}
                >
                  {n.label} <ChevronDown className="h-3.5 w-3.5" />
                </Link>
              ) : (
                <button className={linkClass} type="button">
                  {n.label} <ChevronDown className="h-3.5 w-3.5" />
                </button>
              );
              return (
                <div key={n.label} className="relative group">
                  {trigger}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 hidden group-hover:block">
                    <div className="bg-card border border-border rounded-xl shadow-xl overflow-hidden min-w-[220px]">
                      {n.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block px-4 py-3 text-sm text-foreground hover:bg-secondary hover:text-gold transition"
                          activeProps={{ className: "text-gold bg-secondary" }}
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={n.to}
                to={n.to}
                className={linkClass}
                activeProps={{ className: "text-primary" }}
                activeOptions={{ exact: n.to === "/" }}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center">
          <a href={`tel:${site.phoneRaw}`} className="btn-gold xl:px-6 px-0 xl:w-auto w-12 h-12 xl:h-auto" aria-label={`Call Us at ${site.phone}`}>
            <Phone className="h-4 w-4" />
            <span className="hidden xl:inline">Call Us</span>
          </a>
        </div>

        <button
          className={`xl:hidden p-2 rounded-md min-h-[44px] min-w-[44px] inline-flex items-center justify-center ${lightText ? "text-primary-foreground" : "text-foreground"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden fixed inset-x-0 top-16 lg:top-20 bottom-0 bg-background border-t border-border overflow-y-auto overscroll-contain">
          <div className="container-x py-4 pb-10 flex flex-col gap-1">
            {nav.map((n) => {
              if (n.children) {
                return <MobileDropdown key={n.label} label={n.label} items={n.children} />;
              }
              return (
                <Link key={n.to} to={n.to} className="py-3 px-2 rounded-md text-foreground hover:bg-secondary font-medium">
                  {n.label}
                </Link>
              );
            })}
            <a href={`tel:${site.phoneRaw}`} className="btn-gold mt-4"><Phone className="h-4 w-4" /> Call {site.phone}</a>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileDropdown({ label, items }: { label: string; items: { to: string; label: string }[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center justify-between py-3 px-2 rounded-md text-foreground hover:bg-secondary font-medium"
      >
        <span>{label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="flex flex-col pl-3 border-l border-border ml-2 mt-1 mb-2">
          {items.map((c) => (
            <Link key={c.to} to={c.to} className="py-2.5 px-3 rounded-md text-sm text-foreground hover:bg-secondary">
              {c.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
